import React, { useEffect, useState, useMemo } from "react";
import "./table.css";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { api } from "../../../../../../api";
import ScaleLoader_ from "../../../components/ScaleLoader";
import { AssessementContext } from "../../../AssessmentContext";
import { toast } from "react-hot-toast";
import CheckCompatibility from "./../../../components/CheckCompatibility";
import UnlockAssessmentModal from "./unlockAssessmentModal";
import { useSelector, useDispatch } from "react-redux";
import {
  clearFailedQuestions,
  clearFlaggedQuestion,
  clearRecentActiveExamData,
  flushSubmittedAnswers,
} from "../../../../../../features/candidate-exam/candidate-exam-slice";
import { UpdatedRequestTable } from '../../../../../../components/table/RequestTable';
import { useTranslation } from 'react-i18next';
import PreAssessment from '../../../../../../components/PreAssessment/PreAssessment';
import { formatPipelineTime, hasPassed, setPipelineEndsAt } from '../../../../../../utils/pipelineWindow';

/* Pipeline end time and start window notice, only present when the pipeline has a start window set */
const PipelineWindowNotice = ({ exam }) => {
  if (!exam?.pipeline_ends_at) return null;
  return (
    <span className="block text-xs mt-1">
      <span className="text-gray-600">
        This assessment closes at {formatPipelineTime(exam.pipeline_ends_at)}
      </span>
      {hasPassed(exam?.start_window_closes_at) && (
        <span className="block text-red-500">
          New starts closed at {formatPipelineTime(exam.start_window_closes_at, { withDate: false })}
        </span>
      )}
    </span>
  );
};

const CandidateExams = () => {

  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const previousRoute = location.state?.from;
  const { assessment, setAssessment } = React.useContext(AssessementContext);
  const storedUser = JSON.parse(sessionStorage.getItem('user'));
  const user_id = storedUser?.user?.user_id || sessionStorage.getItem('user_id');
  const candidate_name = storedUser?.user?.name || storedUser?.name;
  const [showModal, setShowModal] = React.useState(false);
  const [examsLoading, setExamsLoading] = useState(false);
  const [preAssessmentOpen, setPreAssessmentOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { startedExams } = useSelector((state) => state.candidateExam);
  const dispatch = useDispatch();

  const {
    isLoading,
    data: pipelineExams,
    refetch,
    isError,
    error,
  } = useQuery(
    "get-pipeline-exams-data",
    async () => {
      const data = await api.getAssesments({ id, user_id });
      return data;
    },
    {
      enabled: !!id
    }
  );

  //console.log("pipelineExams", pipelineExams);

  React.useEffect(() => {
    dispatch(clearFailedQuestions());
  }, []);

  const proceedToExam = (row) => {
    if (!row) return;
    window.sessionStorage.setItem("active-assessment-id", row?.assessment_id);
    sessionStorage.setItem("active-exam-id", row?.exam_id)
    window.sessionStorage.setItem("active-assessment-user-id", row?.user_exam_id);
    window.sessionStorage.setItem("active-assessment", JSON.stringify(row));
    window.sessionStorage.setItem("time_left", row?.time_left);
    window.sessionStorage.setItem("duration", row?.exam_duration);
    window.sessionStorage.setItem("ql", row?.number_of_questions);
    window.sessionStorage.setItem("starting-index", row?.number_of_questions_answered);
    setPipelineEndsAt(row?.pipeline_ends_at);
    navigate("/candidate/assessments/instructions");
  };

  const handleActionClick = (row) => {
    if (!row?.number_of_attempts_allowed || (
      row?.number_of_attempts >= row?.number_of_attempts_allowed
    )) return toast.error(t('attempt-limit-reached'))

    setSelectedRow(row);

    if (row?.allow_proctoring) {
      const needsSnapshots = !row?.has_pre_assessment_snapshot;
      const needsAudio = !row?.has_baseline_audio_recording;

      if (!needsSnapshots && !needsAudio) {
        proceedToExam(row);
      } else {
        setPreAssessmentOpen(true);
      }
    } else {
      proceedToExam(row);
    }
  };

  const handlePreAssessmentComplete = () => {
    setPreAssessmentOpen(false);
    proceedToExam(selectedRow);
  };

  const standaloneExam = sessionStorage.getItem("view");

  // Extract the exams array from the response
  const examsData = pipelineExams?.data || [];

  let columns = useMemo(
    () => [
      {
        title: t('id'),
        selector: null,
        key: "id",
        render: ({ index }) => index + 1,
      },
      {
        title: 'Assessments',
        selector: "exam_name",
        key: "exam_name",
        render: ({ item, row }) => {
          return (
            <>
              <button
                className="text-baseFont underline text-primary"
                onClick={() => handleActionClick(row)}
              >
                {item}
              </button>
              <PipelineWindowNotice exam={row} />
            </>
          );
        },
      },
      {
        title: t('duration'),
        selector: "exam_duration",
        key: "exam_duration",
        render: ({ item }) => {
          return <span>{item} mins</span>
        }
      },
      {
        title: t('questions'),
        selector: "number_of_questions",
        key: "number_of_questions",
        render: ({ item }) => {
          return <span>{item}</span>
        }
      },
      {
        title: 'Current Attempt',
        selector: "current_attempt",
        key: "current_attempt",
        render: ({ item }) => {
          return <span>{item}</span>
        }
      },
      {
        title: 'Attempts Allowed',
        selector: "number_of_attempts_allowed",
        key: "number_of_attempts_allowed",
        render: ({ item }) => {
          return <span>{item}</span>
        }
      },
      {
        title: t('action'),
        selector: "action",
        key: "action",
        render: ({ item, row }) => {
          return (
            <button
              className="bg-primary rounded-full text-white py-2 px-5 text-sm"
              onClick={() => handleActionClick(row)}
            >
              {t('start-exam')}
            </button>
          );
        }
      },
    ], [])

  // if (isLoading || examsLoading) return <ScaleLoader_ />;

  return (
    <>
      <div className="bg-white">
        <section className="jumbotron bg-white p-4">
          {standaloneExam !== "viewStandaloneExam" && (
            <>
              <Link
                to={"/candidate/assessments/view-assessments"}
                className="text-lg"
              >
                Exams
              </Link>
              <span className="mx-2">/</span>
              <Link
                to={"/candidate/assessments/view-assessments"}
                className="text-lg"
              >
                View Exams
              </Link>
              <span className="mx-2">/</span>
              <Link
                to={`/candidate/assessments/candidate-exams/${id}`}
                className="text-lg text-customGreen"
              >
                Candidate exam
              </Link>
            </>
          )}

          <div className="text-white bg-darkBlue mt-8 mb-5 py-5 px-4 mx-1 rounded-lg">
            Candidate Exam &nbsp;{" "}
            {examsData?.length
              ? `(${examsData?.length})`
              : null}
          </div>

          {/* Desktop Table View */}
          <div className='p-3 rounded-lg bg-white'>
            <div className='max-[500px]:hidden'>
              <UpdatedRequestTable
                columns={columns}
                data={examsData}
                isLoading={isLoading}
              />
            </div>

            {/* Mobile Card View */}
            <div className='md:hidden grid grid-cols-1 gap-5'>
              {
                examsData?.length ?
                  examsData?.map((exam, index) => {
                    return (
                      <ul key={exam?.user_exam_id} className="border border-gray-200 rounded-lg mb-8 p-0">
                        <li className="flex justify-between py-3 px-4">
                          <span className="text-lg">{t('exam-name')}:&nbsp;</span>
                          <span className="text-lg text-primary underline"
                            onClick={() => handleActionClick(exam)}
                          >
                            {exam?.exam_name}
                          </span>
                        </li>
                        {exam?.pipeline_ends_at && (
                          <li className="py-2 px-4">
                            <PipelineWindowNotice exam={exam} />
                          </li>
                        )}
                        <li className="flex justify-between bg-gray-100 py-4 px-4">
                          <span className="text-lg">{t('duration')}:&nbsp;</span>
                          <span className="text-lg">
                            {exam?.exam_duration} mins
                          </span>
                        </li>
                        <li className="flex justify-between py-4 px-4">
                          <span className="text-lg">{t('questions')}:&nbsp;</span>
                          <span className={`text-lg`}>
                            {exam?.number_of_questions}
                          </span>
                        </li>
                        <li className="flex justify-between bg-gray-100 py-4 px-4">
                          <span className="text-lg">Current Attempt:&nbsp;</span>
                          <span className={`text-lg`}>
                            {exam?.current_attempt}
                          </span>
                        </li>
                        <li className="flex justify-between py-4 px-4">
                          <span className="text-lg">Attempts Allowed:&nbsp;</span>
                          <span className={`text-lg`}>
                            {exam?.number_of_attempts_allowed}
                          </span>
                        </li>
                        <li className="flex justify-between bg-gray-100 py-4 px-4">
                          <span className="text-lg">{t('action')}:&nbsp;</span>
                          <button
                            className="bg-primary rounded-full text-white py-2 px-5 text-sm"
                            onClick={() => handleActionClick(exam)}
                          >
                            {console.log('Exam Row Data:', exam)}
                            {t('start-exam')}
                          </button>
                        </li>
                      </ul>
                    )
                  })
                  : null
              }
            </div>
          </div>
        </section>
      </div>

      {/* Pre-Assessment Verification Modal */}
      {selectedRow && (
        <PreAssessment
          open={preAssessmentOpen}
          onClose={() => setPreAssessmentOpen(false)}
          user_id={user_id}
          user_exam_id={selectedRow.user_exam_id}
          assessment_id={selectedRow.assessment_id}
          candidate_name={candidate_name}
          onComplete={handlePreAssessmentComplete}
          hasSnapshots={selectedRow.has_pre_assessment_snapshot}
          hasAudio={selectedRow.has_baseline_audio_recording}
        />
      )}
    </>
  );
};

export default CandidateExams;
